<template>
  <div class="nodes-page">
    <section class="nodes-hero">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">🚀 {{ txt.title }}</h1>
          <p class="hero-subtitle">{{ txt.subtitle }}</p>
          <div class="hero-stats">
            <div class="stat-item">
              <div class="stat-value">5,280+</div>
              <div class="stat-label">{{ txt.stats.nodesActive }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">$12.8M+</div>
              <div class="stat-label">{{ txt.stats.totalStaked }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">24.5%</div>
              <div class="stat-label">{{ txt.stats.avgAPY }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="nodes-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ txt.sectionTitles.chooseTier }}</h2>
          <p class="section-subtitle">{{ txt.sectionTitles.chooseSubtitle }}</p>
        </div>
        
        <div class="nodes-grid">
          <div class="node-card" v-for="node in nodes" :key="node.id" :class="{ 'coming-soon': !node.available }">
            <div class="node-header">
              <div class="node-icon">{{ node.icon }}</div>
              <div class="node-badge" :class="node.badgeClass">{{ node.badgeText }}</div>
            </div>
            
            <div class="node-content">
              <h3 class="node-title">{{ node.title }}</h3>
              <div class="node-price">
                <span class="price-amount">{{ node.price }}</span>
                <span class="price-unit">USDT</span>
              </div>
              <ul class="node-features">
                <li v-for="(feature, idx) in node.features" :key="idx">{{ feature }}</li>
              </ul>
            </div>
            
            <div class="node-footer">
              <button
                v-if="node.available"
                class="node-buy-btn"
                @click="isWalletConnected ? purchaseNode(node) : connectWallet()">
                {{ isWalletConnected ? txt.buy_now : txt.connect_wallet }}
              </button>
              <div v-else class="node-coming-soon">
                <span class="coming-soon-text">{{ txt.coming_soon }}</span>
                <span class="coming-soon-desc">{{ node.comingSoonText }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="nodes-info">
          <p class="info-text">{{ txt.info }}</p>
        </div>
      </div>
    </section>

    <section class="benefits-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ txt.sectionTitles.benefits }}</h2>
          <p class="section-subtitle">{{ txt.sectionTitles.benefitsSubtitle }}</p>
        </div>
        
        <div class="benefits-grid">
          <div class="benefit-card">
            <div class="benefit-icon">💰</div>
            <h3 class="benefit-title">{{ txt.benefits.revenueSharing }}</h3>
            <p class="benefit-desc">{{ txt.benefits.revenueDesc }}</p>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">🗳️</div>
            <h3 class="benefit-title">{{ txt.benefits.governanceRights }}</h3>
            <p class="benefit-desc">{{ txt.benefits.governanceDesc }}</p>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">⚡</div>
            <h3 class="benefit-title">{{ txt.benefits.priorityAccess }}</h3>
            <p class="benefit-desc">{{ txt.benefits.priorityDesc }}</p>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">🛡️</div>
            <h3 class="benefit-title">{{ txt.benefits.enhancedSecurity }}</h3>
            <p class="benefit-desc">{{ txt.benefits.securityDesc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="faq-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ txt.sectionTitles.faq }}</h2>
          <p class="section-subtitle">{{ txt.sectionTitles.faqSubtitle }}</p>
        </div>
        
        <div class="faq-list">
          <details class="faq-item">
            <summary>
              <span class="faq-question">{{ txt.faqItems.howPurchase }}</span>
              <span class="faq-toggle">+</span>
            </summary>
            <div class="faq-answer">
              <p>{{ txt.faqItems.howPurchaseAnswer }}</p>
            </div>
          </details>
          
          <details class="faq-item">
            <summary>
              <span class="faq-question">{{ txt.faqItems.whatWallet }}</span>
              <span class="faq-toggle">+</span>
            </summary>
            <div class="faq-answer">
              <p>{{ txt.faqItems.whatWalletAnswer }}</p>
            </div>
          </details>
          
          <details class="faq-item">
            <summary>
              <span class="faq-question">{{ txt.faqItems.canUpgrade }}</span>
              <span class="faq-toggle">+</span>
            </summary>
            <div class="faq-answer">
              <p>{{ txt.faqItems.canUpgradeAnswer }}</p>
            </div>
          </details>
          
          <details class="faq-item">
            <summary>
              <span class="faq-question">{{ txt.faqItems.refundable }}</span>
              <span class="faq-toggle">+</span>
            </summary>
            <div class="faq-answer">
              <p>{{ txt.faqItems.refundableAnswer }}</p>
            </div>
          </details>
          
          <details class="faq-item">
            <summary>
              <span class="faq-question">{{ txt.faqItems.rewardsDistributed }}</span>
              <span class="faq-toggle">+</span>
            </summary>
            <div class="faq-answer">
              <p>{{ txt.faqItems.rewardsDistributedAnswer }}</p>
            </div>
          </details>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import DICT from '../locales/data.js';

export default {
  props: ['lang', 'address'],
  data() {
    return {
      // 节点类型定义，数据通过计算属性动态获取
      nodeTypes: [
        { key: 'whitelist', icon: '👑', available: true },
        { key: 'small', icon: '📦', available: true },
        { key: 'medium', icon: '🏢', available: true },
        { key: 'large', icon: '🏛️', available: false },
        { key: 'super', icon: '🚀', available: false }
      ]
    }
  },
  computed: {
    txt() { return DICT[this.lang].home.nodes },
    isWalletConnected() {
      return !!this.address;
    },
    walletAddress() {
      return this.address || '';
    },
    nodes() {
      return this.nodeTypes.map((nodeType, index) => {
        const nodeKey = nodeType.key;
        const nodeData = this.txt[nodeKey];
        
        return {
          id: index + 1,
          title: nodeData.name,
          icon: nodeType.icon,
          price: nodeData.price.toString(),
          badgeClass: `badge-${nodeKey}`,
          badgeText: this.txt.badges ? this.txt.badges[nodeKey] : nodeKey.charAt(0).toUpperCase() + nodeKey.slice(1),
          available: nodeType.available,
          features: this.txt.features ? this.txt.features[nodeKey] : [],
          comingSoonText: !nodeType.available ? this.txt.coming_soon : ''
        };
      });
    }
  },
   
  mounted() {
    // 不再需要本地检查，因为状态来自父组件
    // 但仍然监听钱包变化以保持同步
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', this.handleAccountsChanged);
      window.ethereum.on('chainChanged', this.handleChainChanged);
    }
  },
   
  beforeDestroy() {
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', this.handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', this.handleChainChanged);
    }
  },
   
  methods: {
    handleAccountsChanged(accounts) {
      // 当账户变化时，通知父组件更新
      if (accounts && accounts.length > 0) {
        // 父组件会更新 address prop
      } else {
        // 钱包断开连接，父组件会处理
      }
    },
    
    handleChainChanged() {
      // 链变化时，父组件会处理
    },
    
    async purchaseNode(node) {
      if (!this.isWalletConnected) {
        alert(this.txt.connect_first);
        return;
      }
      
      if (!node.available) {
        alert(this.txt.not_available);
        return;
      }
      
      // 确认购买
      const confirmMsg = this.txt.confirm_purchase
        .replace('{node}', node.title)
        .replace('{price}', node.price);
      
      if (!confirm(confirmMsg)) {
        return;
      }
      
      try {
        // 触发购买节点事件，让父组件处理转账
        this.$emit('purchase-node', {
          nodeId: node.id,
          nodeTitle: node.title,
          amount: parseFloat(node.price),
          currency: 'USDT'
        });
        
        // 显示处理中的提示
        alert(this.txt.processing);
      } catch (error) {
        console.error('Purchase failed:', error);
        alert(this.txt.purchase_failed);
      }
    },
    
    connectWallet() {
      console.log('Nodes.vue: connectWallet called');
      // 触发父组件的钱包连接
      this.$emit('connect-wallet');
    }
  }
}
</script>

<style scoped>
.nodes-page {
  padding-bottom: 0;
  overflow-x: hidden;
}

/* Hero 区域 */
.nodes-hero {
  padding: 80px 0 60px;
  background: linear-gradient(180deg, #0a0b10 0%, #08090c 100%);
  position: relative;
  overflow: hidden;
}

.nodes-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #00D092, transparent);
}

.hero-content {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.hero-title {
  font-size: 56px;
  font-weight: 800;
  background: linear-gradient(to right, #00D092, #00A878);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 20px;
}

.hero-subtitle {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.6;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 60px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 30px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 32px;
  font-weight: 800;
  font-family: 'Inter', sans-serif;
  letter-spacing: -1px;
  color: #00D092;
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  margin-top: 5px;
}

/* 节点购买部分 */
.nodes-section {
  padding: 80px 0;
  background: #08090c;
}

.section-header {
  text-align: center;
  margin-bottom: 50px;
}

.section-title {
  font-size: 42px;
  font-weight: 800;
  background: linear-gradient(to right, #00D092, #00A878);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 15px;
}

.section-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.nodes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.node-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 25px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.node-card:hover {
  transform: translateY(-5px);
  border-color: rgba(0, 208, 146, 0.3);
  box-shadow: 0 10px 30px rgba(0, 208, 146, 0.1);
}

.node-card.coming-soon {
  opacity: 0.7;
  filter: grayscale(0.3);
}

.node-card.coming-soon:hover {
  transform: none;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

.node-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.node-icon {
  font-size: 40px;
  line-height: 1;
}

.node-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-whitelist {
  background: rgba(255, 215, 0, 0.15);
  color: #FFD700;
  border: 1px solid rgba(255, 215, 0, 0.3);
}

.badge-small {
  background: rgba(0, 208, 146, 0.15);
  color: #00D092;
  border: 1px solid rgba(0, 208, 146, 0.3);
}

.badge-medium {
  background: rgba(66, 133, 244, 0.15);
  color: #4285F4;
  border: 1px solid rgba(66, 133, 244, 0.3);
}

.badge-large {
  background: rgba(255, 109, 0, 0.15);
  color: #FF6D00;
  border: 1px solid rgba(255, 109, 0, 0.3);
}

.badge-super {
  background: rgba(156, 39, 176, 0.15);
  color: #9C27B0;
  border: 1px solid rgba(156, 39, 176, 0.3);
}

.node-content {
  margin-bottom: 25px;
}

.node-title {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
}

.node-price {
  display: flex;
  align-items: baseline;
  margin-bottom: 20px;
}

.price-amount {
  font-size: 36px;
  font-weight: 800;
  color: #00D092;
  margin-right: 8px;
}

.price-unit {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
}

.node-features {
  list-style: none;
  padding: 0;
  margin: 0;
}

.node-features li {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 10px;
  padding-left: 20px;
  position: relative;
}

.node-features li:before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #00D092;
  font-weight: bold;
}

.node-footer {
  margin-top: 20px;
}

.node-buy-btn {
  width: 100%;
  background: linear-gradient(135deg, #00D092, #00A878);
  border: none;
  color: #000;
  padding: 14px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.3s;
  font-size: 16px;
}

.node-buy-btn:hover {
  background: linear-gradient(135deg, #00b881, #008c66);
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 208, 146, 0.3);
}

.node-buy-btn.btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.node-buy-btn.btn-disabled:hover {
  transform: none;
  box-shadow: none;
}

.node-coming-soon {
  text-align: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.coming-soon-text {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #FFD700;
  margin-bottom: 5px;
}

.coming-soon-desc {
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.nodes-info {
  text-align: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.info-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* 权益部分 */
.benefits-section {
  padding: 80px 0;
  background: linear-gradient(180deg, #08090c 0%, #0a0b10 100%);
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.benefit-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
  border-color: rgba(0, 208, 146, 0.3);
  box-shadow: 0 10px 30px rgba(0, 208, 146, 0.1);
}

.benefit-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.benefit-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
}

.benefit-desc {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* FAQ 部分 */
.faq-section {
  padding: 80px 0;
  background: #08090c;
}

.faq-list {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin-bottom: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.faq-item:hover {
  border-color: rgba(0, 208, 146, 0.3);
}

.faq-item summary {
  padding: 20px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  list-style: none;
  outline: none;
}

.faq-question {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  flex: 1;
}

.faq-toggle {
  font-size: 20px;
  color: #00D092;
  font-weight: bold;
  transition: transform 0.3s;
}

.faq-item[open] .faq-toggle {
  transform: rotate(45deg);
}

.faq-answer {
  padding: 0 20px 20px 20px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
}

.faq-answer p {
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .hero-title {
    font-size: 36px;
  }
  
  .hero-subtitle {
    font-size: 16px;
  }
  
  .hero-stats {
    flex-direction: column;
    gap: 30px;
  }
  
  .section-title {
    font-size: 32px;
  }
  
  .nodes-grid {
    grid-template-columns: 1fr;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
  }
}
</style>